import { useState, useEffect, useCallback } from 'react';
import { Save, Clock, FileText, Activity, Droplet, Utensils, Wind, Scissors, Dumbbell, TrendingUp, Edit2, Trash2, History, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { nursesApi } from '../../lib/api/nurses';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';
import { format, parseISO, isValid } from 'date-fns';

interface TaskField {
  label: string;
  hasDropdown: boolean;
  dropdownOptions?: string[];
  resultPlaceholder?: string;
  resultType?: 'text' | 'number' | 'time' | 'date';
}

interface TaskData {
  [key: string]: {
    dropdown?: string;
    result?: string;
    timeTaken?: string;
    comments?: string;
  };
}

interface TaskRecord {
  id: number;
  task_category: string;
  task_data: TaskData;
  recorded_at: string;
  notes?: string;
  nurse_name?: string;
  created_at?: string;
}

interface NurseTaskManagementProps {
  patientId: number;
  patientName: string;
}

// 🔧 FIX #4: Safe date formatting utility
const formatSafeDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, 'PPP • p');
    }
    return 'Invalid Date';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

export function NurseTaskManagement({ patientId, patientName }: NurseTaskManagementProps) {
  const [activeTab, setActiveTab] = useState('vitals');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
  const [deletingRecordId, setDeletingRecordId] = useState<number | null>(null);

  // Historical records for current tab
  const [taskRecords, setTaskRecords] = useState<TaskRecord[]>([]);

  // State for all tabs
  const [vitalsData, setVitalsData] = useState<TaskData>({});
  const [chestDrainData, setChestDrainData] = useState<TaskData>({});
  const [nutritionData, setNutritionData] = useState<TaskData>({});
  const [tracheostomyData, setTracheostomyData] = useState<TaskData>({});
  const [woundDressingData, setWoundDressingData] = useState<TaskData>({});
  const [physiotherapyData, setPhysiotherapyData] = useState<TaskData>({});
  const [intakeOutputData, setIntakeOutputData] = useState<TaskData>({});

  // Define tasks for each tab (same as before)
  const vitalsTasks: Record<string, TaskField> = {
    'Temp': { label: 'Temperature (°F)', hasDropdown: false, resultPlaceholder: 'Enter temperature', resultType: 'number' },
    'HR': { label: 'Heart Rate (bpm)', hasDropdown: false, resultPlaceholder: 'Enter heart rate', resultType: 'number' },
    'RR': { label: 'Respiratory Rate (per min)', hasDropdown: false, resultPlaceholder: 'Enter respiratory rate', resultType: 'number' },
    'SPO2': { label: 'SpO2 (%)', hasDropdown: false, resultPlaceholder: 'Enter SpO2', resultType: 'number' },
    'BP': { label: 'Blood Pressure (mmHg)', hasDropdown: false, resultPlaceholder: 'e.g., 120/80', resultType: 'text' },
    'Breathing Pattern': { label: 'Breathing Pattern', hasDropdown: true, dropdownOptions: ['Normal', 'Labored', 'Shallow', 'Wheeze', 'Crackles'], resultPlaceholder: 'Additional notes' },
    'Cough': { label: 'Cough', hasDropdown: true, dropdownOptions: ['None', 'Dry', 'Productive'], resultPlaceholder: 'Additional notes' },
    'Sputum Color': { label: 'Sputum Color', hasDropdown: true, dropdownOptions: ['Clear', 'White', 'Yellow', 'Green', 'Rusty', 'Bloody'], resultPlaceholder: 'Additional notes' },
    'Oxygen Requirement': { label: 'Oxygen Requirement', hasDropdown: true, dropdownOptions: ['Room Air', 'O₂ L/min'], resultPlaceholder: 'If O₂, specify L/min' },
    'Device': { label: 'Device', hasDropdown: true, dropdownOptions: ['Nasal Cannula', 'Face Mask', 'Non-Rebreather Mask', 'Venturi Mask', 'None'], resultPlaceholder: 'Device details' },
    'Signs of Rejection/Infection': { label: 'Signs of Rejection/Infection', hasDropdown: true, dropdownOptions: ['Fever', 'Increased sputum', 'SOB', 'Low SpO₂', 'Fatigue', 'None'], resultPlaceholder: 'Additional observations' },
  };

  const chestDrainTasks: Record<string, TaskField> = {
    'Drain Side': { label: 'Drain Side', hasDropdown: true, dropdownOptions: ['Right', 'Left', 'Bilateral'], resultPlaceholder: 'Additional notes' },
    'Drain Volume': { label: 'Drain Volume (ml)', hasDropdown: false, resultPlaceholder: 'Enter volume in ml', resultType: 'number' },
    'Drain Color': { label: 'Drain Color', hasDropdown: true, dropdownOptions: ['Serous', 'Sanguinous', 'Hemorrhagic', 'Purulent', 'Chylous'], resultPlaceholder: 'Additional notes' },
    'Drain Dressing': { label: 'Drain Dressing', hasDropdown: true, dropdownOptions: ['Yes', 'No'], resultPlaceholder: 'Dressing details' },
    'Water Seal Column Movement': { label: 'Water Seal Column Movement', hasDropdown: true, dropdownOptions: ['None', 'Intermittent', 'Continuous'], resultPlaceholder: 'Additional notes' },
    'Bubbling': { label: 'Bubbling', hasDropdown: true, dropdownOptions: ['None', 'Intermittent', 'Continuous'], resultPlaceholder: 'Additional notes' },
  };

  const nutritionTasks: Record<string, TaskField> = {
    'Appetite': { label: 'Appetite', hasDropdown: true, dropdownOptions: ['Fair', 'Good', 'Poor'], resultPlaceholder: 'Additional notes' },
    'Intake Type': { label: 'Intake Type', hasDropdown: true, dropdownOptions: ['Normal diet orally', 'Soft diet', 'Liquid diet', 'Tube feeding', 'NPO'], resultPlaceholder: 'Diet details' },
    'Diet Remarks': { label: 'Diet Remarks', hasDropdown: false, resultPlaceholder: 'Enter diet remarks', resultType: 'text' },
    'Stool Frequency': { label: 'Stool Frequency', hasDropdown: false, resultPlaceholder: 'e.g., 2 times/day', resultType: 'text' },
  };

  const tracheostomyTasks: Record<string, TaskField> = {
    'Stoma Site Condition': { label: 'Stoma Site Condition', hasDropdown: true, dropdownOptions: ['Clean', 'Redness', 'Swelling', 'Discharge'], resultPlaceholder: 'Additional notes' },
    'Tracheostomy Tube': { label: 'Tracheostomy Tube', hasDropdown: true, dropdownOptions: ['Secure', 'Loose', 'Obstructed', 'Damaged'], resultPlaceholder: 'Additional notes' },
    'Suctioning Done': { label: 'Suctioning Done', hasDropdown: true, dropdownOptions: ['Done', 'Not Done', 'NA'], resultPlaceholder: 'Additional notes' },
    'Frequency': { label: 'Frequency (times/day)', hasDropdown: false, resultPlaceholder: 'Enter frequency', resultType: 'number' },
    'Secretion Type': { label: 'Secretion Type', hasDropdown: true, dropdownOptions: ['Clear', 'Mucoid', 'Purulent', 'Bloody'], resultPlaceholder: 'Additional notes' },
    'Subglottic Suction per 24 hours': { label: 'Subglottic Suction (ml/24hrs)', hasDropdown: false, resultPlaceholder: 'Enter volume in ml', resultType: 'number' },
    'Subglottic Pressure': { label: 'Subglottic Pressure (cmH₂O)', hasDropdown: false, resultPlaceholder: 'Enter pressure', resultType: 'number' },
    'Oxygen Requirement': { label: 'Oxygen Requirement (L/min)', hasDropdown: true, dropdownOptions: ['Yes', 'No'], resultPlaceholder: 'If yes, specify L/min' },
    'Humidification': { label: 'Humidification', hasDropdown: true, dropdownOptions: ['Yes', 'No'], resultPlaceholder: 'Additional notes' },
  };

  const woundDressingTasks: Record<string, TaskField> = {
    'Dressing': { label: 'Dressing', hasDropdown: true, dropdownOptions: ['Done', 'Not Done'], resultPlaceholder: 'Additional notes' },
    'Date of Last Dressing Change': { label: 'Date of Last Dressing Change', hasDropdown: false, resultPlaceholder: 'Select date', resultType: 'date' },
    'Patient Mobility': { label: 'Patient Mobility', hasDropdown: true, dropdownOptions: ['Independent', 'With Assistance', 'Bedridden'], resultPlaceholder: 'Additional notes' },
    'Wound Appearance': { label: 'Wound Appearance', hasDropdown: true, dropdownOptions: ['Dry', 'Soiled', 'Redness', 'Discharge'], resultPlaceholder: 'Additional notes' },
    'Wound Image': { label: 'Wound Image URL', hasDropdown: false, resultPlaceholder: 'Enter image URL or path', resultType: 'text' },
  };

  const physiotherapyTasks: Record<string, TaskField> = {
    'Physiotherapy Given': { label: 'Physiotherapy Given', hasDropdown: true, dropdownOptions: ['Done', 'Not Done'], resultPlaceholder: 'Additional notes' },
    'Ambulation': { label: 'Ambulation', hasDropdown: true, dropdownOptions: ['Done', 'Not Done'], resultPlaceholder: 'Additional notes' },
    'Breathing Exercises': { label: 'Breathing Exercises', hasDropdown: true, dropdownOptions: ['Done', 'Not Done'], resultPlaceholder: 'Additional notes' },
    'Walk Distance': { label: 'Walk Distance (meters)', hasDropdown: false, resultPlaceholder: 'Enter distance in meters', resultType: 'number' },
    'Oxygen Drop While Walking': { label: 'Oxygen Drop While Walking', hasDropdown: true, dropdownOptions: ['Yes', 'No'], resultPlaceholder: 'Additional notes' },
    'FEV1': { label: 'FEV1 (L)', hasDropdown: false, resultPlaceholder: 'Enter FEV1 value', resultType: 'number' },
  };

  const intakeOutputTasks: Record<string, TaskField> = {
    'Total Intake': { label: 'Total Intake (ml)', hasDropdown: false, resultPlaceholder: 'Enter total intake in ml', resultType: 'number' },
    'Total Output': { label: 'Total Output (ml)', hasDropdown: false, resultPlaceholder: 'Enter total output in ml', resultType: 'number' },
    'Total Urine Output': { label: 'Total Urine Output (ml)', hasDropdown: false, resultPlaceholder: 'Enter urine output in ml', resultType: 'number' },
    'Total Drain': { label: 'Total Drain (ml)', hasDropdown: false, resultPlaceholder: 'Enter total drain in ml', resultType: 'number' },
    'RTF Aspiration': { label: 'RTF Aspiration', hasDropdown: false, resultPlaceholder: 'Enter RTF aspiration details', resultType: 'text' },
    'Balance': { label: 'Balance', hasDropdown: true, dropdownOptions: ['Positive', 'Negative'], resultPlaceholder: 'Additional notes' },
    'Balance Amount': { label: 'Balance Amount (ml)', hasDropdown: false, resultPlaceholder: 'Enter balance amount in ml', resultType: 'number' },
    'Medication Adherence List': { label: 'Medication Adherence: List of Medications', hasDropdown: false, resultPlaceholder: 'Enter list of medications', resultType: 'text' },
    'Adherence Percentage': { label: 'Adherence Percentage (%)', hasDropdown: false, resultPlaceholder: 'Enter percentage', resultType: 'number' },
  };

  // Helper function to get data and setter for current tab
  const getTabData = (tab: string): [TaskData, React.Dispatch<React.SetStateAction<TaskData>>] => {
    switch (tab) {
      case 'vitals': return [vitalsData, setVitalsData];
      case 'chest-drain': return [chestDrainData, setChestDrainData];
      case 'nutrition': return [nutritionData, setNutritionData];
      case 'tracheostomy': return [tracheostomyData, setTracheostomyData];
      case 'wound-dressing': return [woundDressingData, setWoundDressingData];
      case 'physiotherapy': return [physiotherapyData, setPhysiotherapyData];
      case 'intake-output': return [intakeOutputData, setIntakeOutputData];
      default: return [vitalsData, setVitalsData];
    }
  };

  // Helper function to get tasks for current tab
  const getTabTasks = (tab: string): Record<string, TaskField> => {
    switch (tab) {
      case 'vitals': return vitalsTasks;
      case 'chest-drain': return chestDrainTasks;
      case 'nutrition': return nutritionTasks;
      case 'tracheostomy': return tracheostomyTasks;
      case 'wound-dressing': return woundDressingTasks;
      case 'physiotherapy': return physiotherapyTasks;
      case 'intake-output': return intakeOutputTasks;
      default: return vitalsTasks;
    }
  };

  // Get tab display name
  const getTabDisplayName = (tab: string): string => {
    switch (tab) {
      case 'vitals': return 'Vitals';
      case 'chest-drain': return 'Chest Drain';
      case 'nutrition': return 'Nutrition';
      case 'tracheostomy': return 'Tracheostomy Care';
      case 'wound-dressing': return 'Wound Dressing';
      case 'physiotherapy': return 'Physiotherapy';
      case 'intake-output': return 'Intake Output';
      default: return tab;
    }
  };

  // 🔧 FIX #3: Wrap in useCallback with proper dependencies  // ✅ FIX #16: Clear stale data on error
  const fetchTaskRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const tabName = getTabDisplayName(activeTab);
      const response = await nursesApi.getNurseTaskData(patientId, tabName, undefined, undefined, 20);
      setTaskRecords(response.records || []);
    } catch (error: any) {
      console.error('Error fetching task records:', error);
      toast.error('Failed to load task history');
      setTaskRecords([]); // ✅ Clear stale data on error
    } finally {
      setIsLoading(false);
    }
  }, [patientId, activeTab]); // Dependencies properly specified

  // 🔧 FIX #2: Add patientId to dependency array
  useEffect(() => {
    if (showHistory) {
      fetchTaskRecords();
    }
  }, [activeTab, showHistory, patientId, fetchTaskRecords]); // Added patientId and fetchTaskRecords

  // ✅ CRITICAL FIX #12 & #18: Clear edit mode when switching tabs to prevent data corruption
  useEffect(() => {
    if (editingRecordId) {
      setEditingRecordId(null);
      // ✅ FIXED: Don't call clearForm() here because it would clear the NEW tab's data
      // Just clear the edit mode flag and let the user's work remain in the old tab
      toast.warning('Edit mode cleared - you switched to a different task category');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Update field value
  const updateField = (taskKey: string, field: 'dropdown' | 'result' | 'timeTaken' | 'comments', value: string) => {
    const [data, setData] = getTabData(activeTab);
    setData(prev => ({
      ...prev,
      [taskKey]: {
        ...prev[taskKey],
        [field]: value
      }
    }));
  };

  // Clear form
  const clearForm = () => {
    const [, setData] = getTabData(activeTab);
    setData({});
    setEditingRecordId(null);
  };

  // Load record for editing
  const handleEdit = (record: TaskRecord) => {
    const [, setData] = getTabData(activeTab);
    setData(record.task_data);
    setEditingRecordId(record.id);
    setShowHistory(false);
    toast.info('Record loaded for editing');
  };

  // ✅ FIX #8: Save current tab data with validation (create or update)
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const [data] = getTabData(activeTab);
      const tabName = getTabDisplayName(activeTab);
      
      // ✅ Validate that form has data
      if (Object.keys(data).length === 0) {
        toast.error('Please enter at least one task value before saving');
        setIsSaving(false);
        return;
      }

      // ✅ Check if at least one field has a meaningful value
      const hasData = Object.values(data).some(taskData => 
        taskData.result || taskData.dropdown || taskData.comments || taskData.timeTaken
      );

      if (!hasData) {
        toast.error('Please enter at least one task value before saving');
        setIsSaving(false);
        return;
      }
      
      // Prepare payload
      const payload = {
        patient_id: patientId,
        task_category: tabName,
        tasks: data,
        recorded_at: new Date().toISOString()
      };

      if (editingRecordId) {
        // Update existing record
        await nursesApi.updateNurseTaskData(editingRecordId, payload);
        toast.success(`${tabName} data updated successfully!`);
      } else {
        // Create new record
        await nursesApi.submitNurseTaskData(payload);
        toast.success(`${tabName} data saved successfully!`);
      }
      
      clearForm();
      if (showHistory) {
        fetchTaskRecords();
      }
    } catch (error: any) {
      console.error('Error saving task data:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save task data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete record
  const handleDelete = async () => {
    if (!deletingRecordId) return;

    try {
      await nursesApi.deleteNurseTaskData(deletingRecordId);
      toast.success('Task record deleted successfully');
      setDeletingRecordId(null);
      fetchTaskRecords();
    } catch (error: any) {
      console.error('Error deleting task record:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete task record');
    }
  };

  // Render a single task row
  const renderTaskRow = (taskKey: string, taskField: TaskField) => {
    const [data] = getTabData(activeTab);
    const taskData = data[taskKey] || {};

    return (
      <div key={taskKey} className="border border-gray-200 rounded-lg p-4 mb-3 bg-white hover:shadow-sm transition-shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Task Label */}
          <div className="lg:col-span-1 flex items-center">
            <Label className="text-[#494949]">{taskField.label}</Label>
          </div>

          {/* Dropdown (if applicable) */}
          {taskField.hasDropdown && taskField.dropdownOptions && (
            <div className="lg:col-span-1">
              <Select
                value={taskData.dropdown || ''}
                onValueChange={(value) => updateField(taskKey, 'dropdown', value)}
              >
                <SelectTrigger className="border-[#2762AC] focus:ring-[#2762AC]">
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                  {taskField.dropdownOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Result Input */}
          <div className={taskField.hasDropdown ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <Input
              type={taskField.resultType || 'text'}
              placeholder={taskField.resultPlaceholder || 'Enter result'}
              value={taskData.result || ''}
              onChange={(e) => updateField(taskKey, 'result', e.target.value)}
              className="border-[#2762AC] focus:ring-[#2762AC]"
            />
          </div>

          {/* Time Taken */}
          <div className="lg:col-span-1">
            <Input
              type="time"
              placeholder="Time taken"
              value={taskData.timeTaken || ''}
              onChange={(e) => updateField(taskKey, 'timeTaken', e.target.value)}
              className="border-[#2762AC] focus:ring-[#2762AC]"
            />
          </div>

          {/* Comments */}
          <div className="lg:col-span-1">
            <Textarea
              placeholder="Comments"
              value={taskData.comments || ''}
              onChange={(e) => updateField(taskKey, 'comments', e.target.value)}
              className="border-[#2762AC] focus:ring-[#2762AC] min-h-[40px] max-h-[80px]"
              rows={1}
            />
          </div>
        </div>
      </div>
    );
  };

  // Get icon for tab
  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'vitals': return Activity;
      case 'chest-drain': return Droplet;
      case 'nutrition': return Utensils;
      case 'tracheostomy': return Wind;
      case 'wound-dressing': return Scissors;
      case 'physiotherapy': return Dumbbell;
      case 'intake-output': return TrendingUp;
      default: return FileText;
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="mb-6 shadow-lg border-t-4 border-t-[#2762AC]">
          <CardHeader className="bg-gradient-to-r from-[#2762AC] to-[#14CAF4] text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Nurse Task Management</CardTitle>
                <p className="text-sm mt-1 opacity-90">Patient: {patientName}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="bg-white text-[#2762AC] hover:bg-white/90 border-white"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <History className="w-4 h-4 mr-2" />
                  {showHistory ? 'Hide' : 'Show'} History
                </Button>
                {editingRecordId && (
                  <Button
                    variant="outline"
                    className="bg-white text-[#F07D13] hover:bg-white/90 border-white"
                    onClick={clearForm}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel Edit
                  </Button>
                )}
              </div>
            </div>
            {editingRecordId && (
              <Alert className="mt-3 bg-[#F07D13] border-[#F07D13] text-white">
                <AlertDescription>
                  <strong>Editing Mode:</strong> You are currently editing an existing record. Click "Save" to update or "Cancel Edit" to discard changes.
                </AlertDescription>
              </Alert>
            )}
          </CardHeader>
        </Card>

        {/* History View */}
        {showHistory && (
          <Card className="mb-6 shadow-xl border-l-4 border-l-[#14CAF4]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-[#14CAF4]" />
                Task History - {getTabDisplayName(activeTab)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingState message="Loading task history..." />
              ) : taskRecords.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No task records found"
                  description="No historical records for this task category. Start by recording new data below."
                />
              ) : (
                <div className="space-y-3">
                  {taskRecords.map((record) => (
                    <Card key={record.id} className="border-l-4 border-l-[#2762AC] hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-[#2762AC] text-white">
                                {/* 🔧 FIX #4: Use safe date formatting */}
                                {formatSafeDate(record.recorded_at)}
                              </Badge>
                              {record.nurse_name && (
                                <span className="text-sm text-gray-600">by {record.nurse_name}</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                              {/* 🔧 FIX #1: Add type safety */}
                              {Object.entries(record.task_data).slice(0, 6).map(([key, value]) => {
                                // Type guard to ensure value has expected structure
                                const taskValue = value as { dropdown?: string; result?: string };
                                return (
                                  <div key={key} className="text-gray-700">
                                    <span className="font-medium">{key}:</span>{' '}
                                    {taskValue.result || taskValue.dropdown || 'N/A'}
                                  </div>
                                );
                              })}
                            </div>
                            {record.notes && (
                              <p className="text-sm text-gray-600 mt-2 italic">Note: {record.notes}</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1"
                              onClick={() => handleEdit(record)}
                            >
                              <Edit2 className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeletingRecordId(record.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Multi-Tab Interface */}
        <Card className="shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-gray-200 bg-white rounded-t-lg px-4 pt-4 overflow-x-auto">
              <TabsList className="inline-flex h-auto p-0 bg-transparent gap-2 flex-wrap">
                {['vitals', 'chest-drain', 'nutrition', 'tracheostomy', 'wound-dressing', 'physiotherapy', 'intake-output'].map((tab) => {
                  const Icon = getTabIcon(tab);
                  return (
                    <TabsTrigger
                      key={tab}
                      value={tab}
                      className="data-[state=active]:bg-[#2762AC] data-[state=active]:text-white data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-[#494949] rounded-lg px-4 py-2 transition-all hover:bg-[#2762AC]/10"
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {getTabDisplayName(tab)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            {/* Tab Contents */}
            {['vitals', 'chest-drain', 'nutrition', 'tracheostomy', 'wound-dressing', 'physiotherapy', 'intake-output'].map((tab) => (
              <TabsContent key={tab} value={tab} className="m-0">
                <CardContent className="p-6">
                  <ScrollArea className="h-[calc(100vh-350px)] pr-4">
                    <div className="space-y-4">
                      {/* Column Headers */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 pb-2 border-b border-gray-300">
                        <div className="lg:col-span-1 text-sm text-[#494949]">Task Name</div>
                        {Object.values(getTabTasks(tab))[0]?.hasDropdown && (
                          <div className="lg:col-span-1 text-sm text-[#494949]">Selection</div>
                        )}
                        {/* 🔧 FIX #5: Merge duplicate className into one */}
                        <div className={`${Object.values(getTabTasks(tab))[0]?.hasDropdown ? 'lg:col-span-1' : 'lg:col-span-2'} text-sm text-[#494949]`}>
                          Result / Value
                        </div>
                        <div className="lg:col-span-1 text-sm text-[#494949]">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Time Taken
                        </div>
                        <div className="lg:col-span-1 text-sm text-[#494949]">
                          <FileText className="w-4 h-4 inline mr-1" />
                          Comments
                        </div>
                      </div>

                      {/* Task Rows */}
                      {Object.entries(getTabTasks(tab)).map(([key, field]) =>
                        renderTaskRow(key, field)
                      )}
                    </div>
                  </ScrollArea>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                    {editingRecordId ? (
                      <Button variant="outline" onClick={clearForm} className="gap-2">
                        <X className="w-4 h-4" />
                        Cancel Edit
                      </Button>
                    ) : (
                      <div />
                    )}
                    <div className="flex gap-2">
                      {Object.keys(getTabData(activeTab)[0]).length > 0 && (
                        <Button
                          variant="outline"
                          onClick={clearForm}
                        >
                          Clear Form
                        </Button>
                      )}
                      <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-[#2762AC] hover:bg-[#1d4d87] text-white px-8 py-2 rounded-lg shadow-md transition-all"
                        size="lg"
                      >
                        <Save className="w-5 h-5 mr-2" />
                        {isSaving ? 'Saving...' : editingRecordId ? `Update ${getTabDisplayName(tab)} Data` : `Save ${getTabDisplayName(tab)} Data`}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </TabsContent>
            ))}
          </Tabs>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingRecordId} onOpenChange={() => setDeletingRecordId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this task record from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}